import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface PortalDropdownProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  align?: 'left' | 'right';
  onOpen?: (close: () => void) => void;
  onClose?: () => void;
}

const PortalDropdown: React.FC<PortalDropdownProps> = ({
  trigger,
  children,
  className = '',
  align = 'right',
  onOpen,
  onClose
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const calculatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const dropdownHeight = 280; // Estimated dropdown height
      const dropdownWidth = 220; // Estimated dropdown width
      
      let top = rect.bottom;
      let left = rect.left;
      
      // If not enough space below, open upward
      if (rect.bottom + dropdownHeight > viewportHeight - 20) {
        top = rect.top - dropdownHeight;
      }
      
      // Adjust horizontal position based on alignment
      if (align === 'right') {
        left = rect.right - dropdownWidth;
        // Ensure it doesn't go off the left edge
        if (left < 10) {
          left = 10;
        }
      } else {
        // Ensure it doesn't go off the right edge
        if (left + dropdownWidth > viewportWidth - 10) {
          left = viewportWidth - dropdownWidth - 10;
        }
      }
      
      setPosition({
        top,
        left,
        width: dropdownWidth
      });
    }
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen) {
      calculatePosition();
      onOpen?.(() => setIsOpen(false));
    } else {
      onClose?.();
    }
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideTrigger = triggerRef.current && !triggerRef.current.contains(target);
      const isOutsideDropdown = !(target as Element).closest('.portal-dropdown-menu');
      
      if (isOutsideTrigger && isOutsideDropdown) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Recalculate position on scroll
  useEffect(() => {
    if (isOpen) {
      const handleScroll = () => calculatePosition();
      window.addEventListener('scroll', handleScroll, true);
      return () => window.removeEventListener('scroll', handleScroll, true);
    }
  }, [isOpen]);

  return (
    <div className={`portal-dropdown-container ${className}`} ref={triggerRef}>
      <div onClick={handleToggle}>
        {trigger}
      </div>
      
      {isOpen && createPortal(
        <div 
          className="portal-dropdown-menu"
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: position.width,
            zIndex: 9999
          }}
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  );
};

export default PortalDropdown;
